import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';

import Sidebar from 'components/Sidebar';
import SidebarCommunityNewFeaturesRequestsFeedback from 'components/Sidebar/CommunityNewFeaturesRequestsFeedback';
import SidebarGoogleAd from 'components/Sidebar/GoogleAd';

import { isOfferCategorySlugValidToActivateGoogleAd } from 'lib/ads';

import OfferRelatedItems from './OfferRelatedItems';
import RelatedOffers from './RelatedOffers';
import StoreInfo from './StoreInfo';

const OfferActionSheet = dynamic(() => import('./OfferActionSheet'), {
  ssr: false,
});

const OfferSidebar = ({ offer }) => {
  const storeInfoRef = useRef(null);
  const sidebarNewFeaturesRef = useRef(null);
  const relatedOffersRef = useRef(null);
  const sidebarFirstGoogleAdRef = useRef(null);
  const offerRelatedItemsRef = useRef(null);

  const isGoogleAdActive = useMemo(
    () => isOfferCategorySlugValidToActivateGoogleAd(offer?.categorySlug),
    [offer?.categorySlug]
  );

  const refsToBeIgnoredBeforeFixing = useMemo(() => {
    const refs = [relatedOffersRef, storeInfoRef, sidebarFirstGoogleAdRef];

    if (isGoogleAdActive) {
      refs.push(sidebarNewFeaturesRef, offerRelatedItemsRef);
    }

    return refs;
  }, [isGoogleAdActive]);

  return (
    <Sidebar refsToBeIgnoredBeforeFixing={refsToBeIgnoredBeforeFixing}>
      {offer.offerRelatedOffers.length > 0 && (
        <RelatedOffers
          offers={offer.offerRelatedOffers}
          offerStatusName={offer.offerStatusName}
          mb={4}
          ref={relatedOffersRef}
        />
      )}
      <StoreInfo ref={storeInfoRef} {...offer} />
      {isGoogleAdActive && (
        <SidebarGoogleAd
          adId="offer_page_sidebar_1_320x50_300x250"
          adPath="/promobit.com.br_offer_page/sidebar_1_320x50_300x250"
          mb={4}
          ref={sidebarFirstGoogleAdRef}
          adProps={{
            height: [50, null, 250],
            width: ['100%', null, 300],
          }}
          adSizes={[
            [320, 50],
            [300, 250],
          ]}
          adResponsiveSizesMap={[
            {
              viewport: [768, 0],
              sizesMap: [300, 250],
            },
            {
              viewport: [0, 0],
              sizesMap: [320, 50],
            },
          ]}
          showAdSlotOnBreakpoints={[true, false, null, true]}
        />
      )}
      <SidebarCommunityNewFeaturesRequestsFeedback
        ref={sidebarNewFeaturesRef}
      />
      <OfferRelatedItems
        mb={isGoogleAdActive ? 4 : null}
        ref={offerRelatedItemsRef}
        {...offer}
      />
      {isGoogleAdActive && (
        <SidebarGoogleAd
          adId="offer_page_sidebar_2_300x250"
          adPath="/promobit.com.br_offer_page/sidebar_2_300x250"
        />
      )}
      <OfferActionSheet {...offer} />
    </Sidebar>
  );
};

OfferSidebar.defaultProps = {
  offer: null,
};

OfferSidebar.propTypes = {
  offer: PropTypes.shape({
    hasPhysicalStore: PropTypes.bool.isRequired,
    isOfferCommentsAllowed: PropTypes.bool.isRequired,
    isOfferPriority: PropTypes.bool.isRequired,
    isStoreCnpjValid: PropTypes.bool.isRequired,
    isStoreVerified: PropTypes.bool.isRequired,
    marketplaceRate: PropTypes.bool,
    offerId: PropTypes.number.isRequired,
    offerKeywords: PropTypes.arrayOf(PropTypes.string).isRequired,
    offerRelatedOffers: PropTypes.arrayOf(
      PropTypes.shape({
        offerId: PropTypes.number.isRequired,
        offerOldPrice: PropTypes.number.isRequired,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerTitle: PropTypes.string.isRequired,
      })
    ).isRequired,
    offerStatusName: PropTypes.string.isRequired,
    relatedSubcategories: PropTypes.arrayOf(
      PropTypes.shape({
        subcategoryId: PropTypes.number.isRequired,
        subcategoryName: PropTypes.string.isRequired,
        subcategorySlug: PropTypes.string.isRequired,
        subcategoryWishlist: PropTypes.string.isRequired,
      })
    ).isRequired,
  }),
};

export default OfferSidebar;
